'use server'

import { apiRequest, ApiResponse } from '../request'

/**
 * Estoque dos PRODUTOS do catálogo.
 *
 * ⚠️ Não confundir com `lib/api/stock/*`, que é o módulo de MEDICAMENTO (lote, validade,
 * dispensação, SNGPC). Produto de catálogo não tem lote e não passa por lá.
 */

export type CatalogStockMovementType = 'IN' | 'OUT'

export type CatalogStockMovementReason =
    | 'PURCHASE'
    | 'ADJUSTMENT'
    | 'INVENTORY'
    | 'SALE_STORE'
    | 'SALE_ORDER'
    | 'RETURN'
    | 'LOSS'

export const STOCK_MOVEMENT_REASON_LABELS: Record<
    CatalogStockMovementReason,
    string
> = {
    PURCHASE: 'Entrada por compra',
    ADJUSTMENT: 'Ajuste manual',
    INVENTORY: 'Inventário',
    SALE_STORE: 'Venda na loja',
    SALE_ORDER: 'Entrega em atendimento',
    RETURN: 'Devolução',
    LOSS: 'Perda',
}

/** O que o operador pode lançar à mão. Venda nasce da cobrança e do atendimento. */
export const MANUAL_STOCK_REASONS: CatalogStockMovementReason[] = [
    'PURCHASE',
    'ADJUSTMENT',
    'INVENTORY',
    'LOSS',
    'RETURN',
]

export interface CatalogStockItem {
    catalogId: number
    name: string
    sku: string | null
    unit: string | null
    stockQuantity: number
    /** Preso em compras da loja que ainda não fecharam. */
    reserved: number
    available: number
    minStock: number | null
    low: boolean
    active: boolean
    updatedAt: string
}

export interface CatalogStockMovement {
    id: number
    catalogId: number
    companyId: number
    type: CatalogStockMovementType
    reason: CatalogStockMovementReason
    quantity: number
    previousQuantity: number
    newQuantity: number
    unitCost: number | null
    referenceType: string | null
    referenceId: number | null
    notes: string | null
    createdAt: string
    catalog: { id: number; name: string }
    user: { id: number; name: string } | null
}

export interface CatalogLowStockItem {
    catalogId: number
    name: string
    stockQuantity: number
    minStock: number
    reserved: number
}

export interface CreateCatalogStockMovementData {
    catalogId: number
    companyId?: number
    reason: CatalogStockMovementReason
    /** Em `INVENTORY` é o saldo CONTADO, não a diferença. */
    quantity: number
    unitCost?: number
    notes?: string
}

export async function getCatalogStock(params?: {
    companyId?: number
    search?: string
    onlyLow?: boolean
    skip?: number
    take?: number
}): Promise<ApiResponse<{ data: CatalogStockItem[]; total: number }>> {
    const queryParams: Record<string, any> = {}
    if (params?.companyId) queryParams.companyId = params.companyId
    if (params?.search) queryParams.search = params.search
    if (params?.onlyLow) queryParams.onlyLow = 'true'
    if (params?.skip != null) queryParams.skip = params.skip
    if (params?.take != null) queryParams.take = params.take
    return apiRequest('catalog-stock', { params: queryParams })
}

export async function getCatalogLowStock(
    companyId?: number
): Promise<ApiResponse<CatalogLowStockItem[]>> {
    return apiRequest('catalog-stock/low', {
        params: companyId ? { companyId } : {},
    })
}

export async function getCatalogStockMovements(params?: {
    companyId?: number
    catalogId?: number
    reason?: CatalogStockMovementReason
    from?: string
    to?: string
    skip?: number
    take?: number
}): Promise<ApiResponse<{ data: CatalogStockMovement[]; total: number }>> {
    const queryParams: Record<string, any> = {}
    if (params?.companyId) queryParams.companyId = params.companyId
    if (params?.catalogId) queryParams.catalogId = params.catalogId
    if (params?.reason) queryParams.reason = params.reason
    if (params?.from) queryParams.from = params.from
    if (params?.to) queryParams.to = params.to
    if (params?.skip != null) queryParams.skip = params.skip
    if (params?.take != null) queryParams.take = params.take
    return apiRequest('catalog-stock/movements', { params: queryParams })
}

export async function createCatalogStockMovement(
    data: CreateCatalogStockMovementData
): Promise<
    ApiResponse<{ previousQuantity: number; newQuantity: number }>
> {
    return apiRequest('catalog-stock/movements', { data }, 'post')
}
